import type { ColumnDef } from "@tanstack/react-table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo } from "react";
import type { ContentRow, DisplayMode } from "../../types";
import styles from "./index.module.scss";

interface BaseTableProps {
  mode: DisplayMode;
  headers: string[];
  rows: string[][];
  labelMap?: Record<string, string> | undefined;
  maxRows?: number | undefined;
  onRowClick?: ((index: number) => void) | undefined;
  selectedRowIndex?: number | null | undefined;
  className?: string | undefined;
}

const BaseTable: React.FC<BaseTableProps> = ({
  mode,
  headers,
  rows,
  labelMap,
  maxRows,
  onRowClick,
  selectedRowIndex,
  className = "",
}) => {
  const { columns, data } = useMemo(() => {
    const columnHelper = createColumnHelper<ContentRow>();
    const resolvedLabelMap = labelMap ?? {};

    const columns: ColumnDef<ContentRow, string>[] = headers.map((header) =>
      columnHelper.accessor(header, {
        id: header,
        header: resolvedLabelMap[header] ?? header,
        cell: (info) => info.getValue(),
      }),
    );

    const limitedRows = maxRows && mode === "compact" ? rows.slice(0, maxRows) : rows;

    const data = limitedRows.map((row) => {
      const rowObj: ContentRow = {};
      headers.forEach((key, idx) => {
        rowObj[key] = row[idx] ?? "";
      });
      return rowObj;
    });

    return { columns, data };
  }, [headers, rows, labelMap, maxRows, mode]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const wrapperClass = mode === "compact" ? styles["wrapper--compact"] : styles["wrapper"];
  const tableClass = mode === "compact" ? styles["table--compact"] : styles["table"];
  const isClickable = mode === "full" && onRowClick;

  return (
    <div className={`${wrapperClass} ${className}`}>
      <table className={tableClass}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row, index) => (
            <tr
              key={row.id}
              className={`${isClickable ? styles["tr--clickable"] : ""} ${
                selectedRowIndex === index ? styles["tr--selected"] : ""
              }`}
              onClick={isClickable ? () => onRowClick(index) : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {mode === "compact" && maxRows && rows.length > maxRows && (
        <div className={styles["moreItems"]}>他 {rows.length - maxRows} 件</div>
      )}
    </div>
  );
};

export default BaseTable;
